import { TypeButton } from '../../utils/button';
import { CardItems, Hvc } from '../../utils/hooks';
import { AlertSVG } from '../../utils/svgs';
import type { IPresenceTally } from './agent-presence';
import type { ursType } from './utils';
import type { IVoteBalance } from './vote-balance';
import { VoteBalanceStrip } from './VoteBalance';

const Preview = ({
	onStage,
	onSubmit,
	load,
	numberofUploadedFile,
	resultsBlocked,
	balance,
	acknowledged,
	onAcknowledge,
	submitError,
	missingFields = [],
	onRetryLookups,
	lookupsLoading,
	presence,
}: {
	onStage: (stage: ursType) => void;
	onSubmit: () => void;
	load: boolean;
	numberofUploadedFile: number;
	resultsBlocked?: boolean;
	/** The sheet's own arithmetic — party sum vs Valid Votes, plus the ballot account. */
	balance: IVoteBalance;
	acknowledged: boolean;
	onAcknowledge: (next: boolean) => void;
	submitError?: string;
	/** Agent-facing names of required details a background lookup hasn't returned yet. */
	missingFields?: string[];
	onRetryLookups?: () => void;
	lookupsLoading?: boolean;
	/** Polling-agent tally; undefined when capture does not apply (collation levels, flag off). */
	presence?: IPresenceTally;
}) => (
	<div className='f-column-17'>
		<div className='f-column-23 border-label rounded p-4 cursor-pointer'>
			<CardItems
				title=''
				value='Election results'
			/>
			<div className='f-row-12 align-items-center'>
				<TypeButton
					title='Preview'
					buttonSize='little'
					buttonType='outlined'
					onClick={() => onStage('Upload Result')}
				/>
				<p className='text-small m-0 color-label'>
					{numberofUploadedFile} file{numberofUploadedFile === 1 ? '' : 's'} uploaded
				</p>
			</div>
		</div>
		<div className='f-column-23 border-label rounded p-4 cursor-pointer'>
			<CardItems
				title=''
				value='Election Summary Data'
			/>
			<TypeButton
				title='Preview'
				buttonSize='little'
				buttonType='outlined'
				onClick={() => onStage('Result Summary')}
			/>
		</div>
		<div className='f-column-23 border-label rounded p-4 cursor-pointer'>
			<CardItems
				title=''
				value='Party Vote Count'
			/>
			<div className='f-row-12 align-items-center'>
				<TypeButton
					title='Preview'
					buttonSize='little'
					buttonType='outlined'
					onClick={() => onStage('Party Vote Count')}
				/>
				<TypeButton
					title='Vote Summary'
					buttonSize='little'
					buttonType='outlined'
					onClick={() => onStage('Party Vote Summary')}
				/>
			</div>
		</div>
		{presence ? (
			<div className='f-column-23 border-label rounded p-4'>
				<CardItems
					title=''
					value='Polling Agents'
				/>
				<p className='text-small m-0 color-label'>
					{presence.present} present · {presence.absent} no agent · {presence.unrecorded} not recorded
				</p>
				{/* Advisory only: an unanswered row is "not recorded", never "no agent", and never blocks Submit. */}
				{presence.unrecorded > 0 ? (
					<p className='text-small m-0 color-label'>
						{presence.unrecorded === presence.total
							? 'None recorded yet. You can still submit; recording them helps your party see where it had no one watching.'
							: 'Parties left unanswered are saved as "not recorded", not as "no agent".'}
					</p>
				) : null}
				<TypeButton
					title='Review'
					buttonSize='little'
					buttonType='outlined'
					onClick={() => onStage('Polling Agents')}
				/>
			</div>
		) : null}
		{/*
			Hard gate. The V2 submission carries this polling unit's ward, LGA, state and zone,
			which arrive from a background lookup. The server accepts a blank one and turns it
			into a result nobody can trace back to a place, so blocking here is the only defence.
		*/}
		<Hvc
			removeDOM
			view={missingFields.length > 0}
		>
			<div
				className='upload-result__gps-gate'
				role='alert'
			>
				<span className='upload-result__gps-gate-icon'>
					<AlertSVG />
				</span>
				<div className='upload-result__gps-gate-copy'>
					<strong>Still loading your polling unit details</strong>
					<p>
						Your {missingFields.join(', ')} {missingFields.length === 1 ? 'is' : 'are'} needed before this result can be
						submitted. On a working connection this clears in a few seconds.
					</p>
					<TypeButton
						title='Check again'
						buttonSize='little'
						buttonType='outlined'
						load={!!lookupsLoading}
						onClick={onRetryLookups}
					/>
				</div>
			</div>
		</Hvc>
		{/* Party-results gate — persistent inline alert, same pattern as the GPS gate. */}
		<Hvc
			removeDOM
			view={!!resultsBlocked}
		>
			<div
				className='upload-result__gps-gate'
				role='alert'
			>
				<span className='upload-result__gps-gate-icon'>
					<AlertSVG />
				</span>
				<div className='upload-result__gps-gate-copy'>
					<strong>Party results required</strong>
					<p>Record votes for at least one party (Party Vote Count) before submitting.</p>
				</div>
			</div>
		</Hvc>
		{/*
			The figures themselves, not a description of them — and a way straight to
			the discrepancy. Submit stays reachable (a real sheet can genuinely
			disagree) but only once the agent says they've checked it.
		*/}
		<Hvc
			removeDOM
			view={!resultsBlocked && balance.needsAcknowledgement}
		>
			<div className='f-column-15'>
				<VoteBalanceStrip
					balance={balance}
					onReview={() => onStage('Party Vote Summary')}
					reviewLabel='Review'
				/>
				<label className='vb-ack'>
					<input
						type='checkbox'
						checked={acknowledged}
						onChange={(e) => onAcknowledge(e.target.checked)}
					/>
					<span>The result sheet itself shows these figures differing.</span>
				</label>
			</div>
		</Hvc>
		{/*
			The server's own rejection reason, held on screen next to the button that produced it.
			The V2 create endpoints reject for reasons an agent can act on — most often "a result
			already exists for this polling unit" — so the message itself is the instruction.
		*/}
		<Hvc
			removeDOM
			view={!!submitError}
		>
			<div
				className='upload-result__gps-gate'
				role='alert'
			>
				<span className='upload-result__gps-gate-icon'>
					<AlertSVG />
				</span>
				<div className='upload-result__gps-gate-copy'>
					<strong>Result not submitted</strong>
					<p>{submitError}</p>
				</div>
			</div>
		</Hvc>
		<div className='mt-4'>
			<TypeButton
				title={submitError ? 'Try submitting again' : 'Submit'}
				buttonSize='small'
				onClick={onSubmit}
				className='w-100'
				load={load}
				disabled={missingFields.length > 0 || (balance.needsAcknowledgement && !acknowledged)}
			/>
		</div>
	</div>
);

export default Preview;
